/**
 * @fileoverview Teammate Invite Modal Component
 * 
 * Modal that appears after a user creates their first API key, prompting
 * them to invite teammates. Pre-fills email domain from current user.
 * 
 * @author Claude Code
 * @since 2024-01-01
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  UserPlus, 
  Mail, 
  Users, 
  CheckCircle,
  X,
  Sparkles
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import { useAuth } from '@/lib/auth/useAuth';
import { useOrganization } from '@/lib/contexts/OrganizationContext';
import { apiClient } from '@/lib/api/client';

/**
 * Props for the TeammateInviteModal component
 */
interface TeammateInviteModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Callback when invites are sent successfully */
  onInvitesSent?: (count: number) => void;
}

/**
 * Teammate Invite Modal Component
 * 
 * Appears after first API key creation to encourage team collaboration.
 * Features:
 * - Pre-filled email domain suggestions
 * - Multiple email input
 * - Team collaboration benefits
 * - Analytics tracking
 * 
 * @param props - Component props
 * @returns Rendered teammate invite modal
 */
const TeammateInviteModal: React.FC<TeammateInviteModalProps> = ({
  isOpen,
  onClose,
  onInvitesSent
}) => {
  const [emails, setEmails] = useState<string[]>(['']);
  const [isInviting, setIsInviting] = useState(false);
  const [invitesSent, setInvitesSent] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();
  const { organization } = useOrganization();

  /**
   * Get suggested email domain from current user
   */
  const getUserDomain = () => {
    if (!user?.email) return '';
    const domain = user.email.split('@')[1];
    return domain || '';
  };

  /**
   * Reset modal state when it opens
   */
  useEffect(() => {
    if (isOpen) {
      setEmails(['']);
      setIsInviting(false);
      setInvitesSent(false);
      
      // Pre-fill first email with domain suggestion
      const domain = getUserDomain();
      if (domain) {
        setEmails([`@${domain}`]);
      }
      
      // Track modal shown
      trackEvent('teammate_invite_modal_shown', {
        user_domain: domain,
        organization_id: organization?.id
      });
    }
  }, [isOpen, user?.email, organization?.id, getUserDomain]);

  /**
   * Add new email input field
   */
  const addEmailField = () => {
    const domain = getUserDomain();
    setEmails([...emails, domain ? `@${domain}` : '']);
  };

  /**
   * Remove email input field
   */
  const removeEmailField = (index: number) => {
    if (emails.length > 1) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  /**
   * Update email at specific index
   */
  const updateEmail = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  /**
   * Validate email format
   */
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Handle sending invites
   */
  const handleSendInvites = async () => {
    // Filter valid emails
    const validEmails = emails.filter(email => 
      email.trim() && isValidEmail(email.trim())
    );

    if (validEmails.length === 0) {
      toast({
        title: "No valid emails",
        description: "Please enter at least one valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsInviting(true);

    try {
      // Send invites via API
      const response = await apiClient.post<{
        success: boolean;
        invited_count: number;
        failed_emails?: string[];
      }>(`/api/organizations/${organization?.id}/members/invite-bulk`, {
        emails: validEmails.map(email => email.trim()),
        firstName: '',
        lastName: ''
      });

      if (response.success && response.data) {
        setInvitesSent(true);
        
        // Track successful invites
        trackEvent('teammate_invites_sent', {
          invite_count: response.data.invited_count,
          failed_count: response.data.failed_emails?.length || 0,
          user_domain: getUserDomain()
        });

        toast({
          title: "Invites sent!",
          description: `Successfully sent ${response.data.invited_count} invitation${response.data.invited_count !== 1 ? 's' : ''}`,
        });

        // Call success callback
        onInvitesSent?.(response.data.invited_count);

        // Auto-close after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(response.error || 'Failed to send invites');
      }
    } catch (error) {
      console.error('Error sending invites:', error);
      toast({
        title: "Error sending invites",
        description: error instanceof Error ? error.message : "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    trackEvent('teammate_invite_modal_closed', {
      invites_sent: invitesSent,
      emails_entered: emails.filter(e => e.trim()).length
    });
    onClose();
  };

  /**
   * Handle skip action
   */
  const handleSkip = () => {
    trackEvent('teammate_invite_modal_skipped', {
      emails_entered: emails.filter(e => e.trim()).length
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {invitesSent ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                Invites Sent!
              </>
            ) : (
              <>
                <UserPlus className="h-5 w-5" />
                Bring Your Team
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {invitesSent ? (
              "Your teammates will receive email invitations to join your EvalOps workspace."
            ) : (
              "You've created your first API key! Now invite your team to collaborate on test automation."
            )}
          </DialogDescription>
        </DialogHeader>

        {!invitesSent ? (
          <div className="space-y-6 mt-6">
            {/* Benefits */}
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <div className="p-1.5 bg-blue-100 dark:bg-blue-900 rounded-md">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Team Collaboration</h4>
                  <p className="text-xs text-muted-foreground">
                    Share test results and alerts across your team
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded-md">
                  <Sparkles className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Better Coverage</h4>
                  <p className="text-xs text-muted-foreground">
                    More eyes on test failures means faster fixes
                  </p>
                </div>
              </div>
            </div>

            {/* Email Inputs */}
            <div className="space-y-3">
              <Label>Team Member Emails</Label>
              {getUserDomain() && (
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-xs">
                    <Mail className="h-3 w-3 mr-1" />
                    @{getUserDomain()}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Suggested domain
                  </span>
                </div>
              )}
              
              {emails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="email"
                    placeholder="teammate@company.com"
                    value={email}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    className="flex-1"
                  />
                  {emails.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEmailField(index)}
                      className="h-9 w-9"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button
                variant="outline"
                size="sm"
                onClick={addEmailField}
                className="w-full"
                disabled={emails.length >= 5}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Another Email
              </Button>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                onClick={handleSendInvites}
                disabled={isInviting}
                className="flex-1"
              >
                {isInviting ? (
                  "Sending..."
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invites
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={isInviting}
              >
                Skip for now
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-sm text-muted-foreground">
              Your team will receive invitation emails shortly. They'll be able to join your workspace and collaborate on test automation!
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TeammateInviteModal;